import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import Activity from "@mapmycustomers/shared/types/entity/Activity";
import User from "@mapmycustomers/shared/types/User";

import UserMultiSelectField from "@app/component/input/UserMultiSelectField";
import useView from "@app/component/view/utils/useView";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import styles from "./QuickAssigneeFilter.module.scss";

interface Props {
  users: User[];
}

const QuickAssigneeFilter: React.FC<Props> = ({ users }) => {
  const analyticIssuer = useAnalytics();
  const {
    updateFilterModel,
    viewState: { filter },
  } = useView<Activity>();
  const intl = useIntl();

  const handleChange = useCallback(
    (userIds: Array<User["id"]>) => {
      updateFilterModel({
        assignee: userIds.length ? { operator: FilterOperator.IN_ANY, value: userIds } : undefined,
      });
    },
    [updateFilterModel]
  );

  const userIds = useMemo(
    () =>
      filter.assignee &&
      isSimpleCondition(filter.assignee) &&
      filter.assignee.operator === FilterOperator.IN_ANY
        ? (filter.assignee.value as Array<User["id"]>)
        : [],
    [filter]
  );

  const handleClick = useCallback(() => {
    analyticIssuer?.clicked(["Assignees Selection"]);
  }, [analyticIssuer]);

  return (
    <UserMultiSelectField
      className={styles.container}
      onChange={handleChange}
      onClick={handleClick}
      oneLiner
      placeholder={intl.formatMessage({
        id: "activityList.filter.assignee.placeholder",
        defaultMessage: "All assignees",
        description: "Placeholder of the Assignee filter on activity list page",
      })}
      size="middle"
      users={users}
      value={userIds}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  users: getUsers(state),
});

export default connect(mapStateToProps)(QuickAssigneeFilter);
